import { useState, useRef, useEffect, useCallback } from "react";
import { Volume2, Play, Pause, X, Headphones, RefreshCw } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default audio URL (original greeting)
const DEFAULT_AUDIO_URL = "https://customer-assets.emergentagent.com/job_3fa63be4-5823-4101-a96a-c8a6765e98a6/artifacts/g7imt14c_ElevenLabs_2026-03-29T12_18_35_Karine_LauzonRN_eleven_multilingual_v2.mp3";

// All greeting section IDs
const GREETING_SECTIONS = [
  { id: "greeting_welcome", title: "Welcome", titleFr: "Bienvenue" },
  { id: "greeting_botox", title: "About Botox", titleFr: "À propos du Botox" },
  { id: "greeting_skinboosters", title: "Skin Boosters", titleFr: "Skin Boosters" },
  { id: "greeting_firsttime", title: "First Time?", titleFr: "Première fois?" },
  { id: "greeting_booknow", title: "Ready to Book", titleFr: "Prêt à réserver" },
  { id: "greeting_locations", title: "Our Locations", titleFr: "Nos cliniques" },
  { id: "greeting_returning", title: "Welcome Back", titleFr: "Bon retour" },
];

// LocalStorage keys
const HEARD_GREETINGS_KEY = "karine_heard_greetings";
const LAST_GREETING_KEY = "karine_last_greeting";

// Get heard greetings from localStorage
const getHeardGreetings = () => {
  try {
    const stored = localStorage.getItem(HEARD_GREETINGS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Mark a greeting as heard
const markGreetingAsHeard = (greetingId) => {
  try {
    const heard = getHeardGreetings();
    if (!heard.includes(greetingId)) {
      heard.push(greetingId);
      localStorage.setItem(HEARD_GREETINGS_KEY, JSON.stringify(heard));
    }
  } catch {}
};

// Get next greeting (prioritize unheard, then random)
const getNextGreeting = (currentId = null) => {
  const heard = getHeardGreetings();
  const lastGreeting = localStorage.getItem(LAST_GREETING_KEY);
  
  // Filter out current and last greeting
  let available = GREETING_SECTIONS.filter(g => 
    g.id !== currentId && g.id !== lastGreeting
  );
  
  // Prioritize unheard greetings
  const unheard = available.filter(g => !heard.includes(g.id));
  
  if (unheard.length > 0) {
    // Pick random unheard
    return unheard[Math.floor(Math.random() * unheard.length)];
  }
  
  if (available.length > 0) {
    // All heard - pick random from available
    return available[Math.floor(Math.random() * available.length)];
  }
  
  // Fallback - pick any random
  return GREETING_SECTIONS[Math.floor(Math.random() * GREETING_SECTIONS.length)];
};

// Small inline play button (for header/about section)
export const KarineAudioButton = ({ size = "sm", className = "", showLabel = true }) => {
  const { language } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  
  useEffect(() => {
    const audio = new Audio(DEFAULT_AUDIO_URL);
    audioRef.current = audio;
    
    const handleTimeUpdate = () => {
      setProgress((audio.currentTime / audio.duration) * 100);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };
    
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    
    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);
  
  const togglePlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };
  
  const sizeClasses = {
    xs: "w-6 h-6",
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12"
  };
  
  const iconSizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };
  
  return (
    <button
      onClick={togglePlay}
      className={`group relative flex items-center gap-2 ${className}`}
      data-testid="karine-audio-button"
      aria-label={isPlaying ? "Pause greeting" : "Play Karine's greeting"}
    >
      <div 
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center transition-all duration-300 ${
          isPlaying 
            ? "bg-amber-500 text-white shadow-lg shadow-amber-500/30" 
            : "bg-amber-100 text-amber-700 hover:bg-amber-200 group-hover:shadow-md"
        }`}
      >
        {isPlaying && (
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray={`${progress} 100`}
              className="text-white/40"
            />
          </svg>
        )}
        {isPlaying ? (
          <Pause className={iconSizes[size]} />
        ) : (
          <Volume2 className={iconSizes[size]} />
        )}
      </div>
      {showLabel && (
        <span className={`text-xs font-medium transition-colors ${
          isPlaying ? "text-amber-600" : "text-gray-600 group-hover:text-amber-600"
        }`}>
          {isPlaying 
            ? (language === "fr" ? "En cours..." : "Playing...") 
            : (language === "fr" ? "Écouter Karine" : "Hear from Karine")}
        </span>
      )}
    </button>
  );
};

// Floating widget for corner of screen - WITH ROTATING GREETINGS
export const KarineAudioFloatingWidget = () => {
  const { language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentGreeting, setCurrentGreeting] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [greetingCount, setGreetingCount] = useState(0);
  const audioRef = useRef(null);
  
  // Load a greeting from the API
  const loadGreeting = useCallback(async (greeting) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/tts/audio/${greeting.id}`);
      if (res.data?.audio_url) {
        // Handle both relative and absolute URLs
        const fullAudioUrl = res.data.audio_url.startsWith('http') 
          ? res.data.audio_url 
          : `${process.env.REACT_APP_BACKEND_URL}${res.data.audio_url}`;
        setAudioUrl(fullAudioUrl);
        setCurrentGreeting(greeting);
        localStorage.setItem(LAST_GREETING_KEY, greeting.id);
        
        // Create new audio element
        if (audioRef.current) {
          audioRef.current.pause();
        }
        const audio = new Audio(fullAudioUrl);
        audioRef.current = audio;
        
        audio.addEventListener("timeupdate", () => {
          setProgress((audio.currentTime / audio.duration) * 100);
        });
        
        audio.addEventListener("ended", () => {
          setIsPlaying(false);
          setProgress(0);
          markGreetingAsHeard(greeting.id);
        });
        
        return true;
      }
    } catch (err) {
      console.log(`Greeting ${greeting.id} not available`);
    } finally {
      setIsLoading(false);
    }
    return false;
  }, []);
  
  // Load initial greeting when expanded
  useEffect(() => {
    if (isExpanded && !currentGreeting && !isLoading) {
      const nextGreeting = getNextGreeting();
      loadGreeting(nextGreeting);
    }
  }, [isExpanded, currentGreeting, isLoading, loadGreeting]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);
  
  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };
  
  const playNextGreeting = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setProgress(0);
    
    const nextGreeting = getNextGreeting(currentGreeting?.id);
    const loaded = await loadGreeting(nextGreeting);
    if (loaded) {
      setGreetingCount(prev => prev + 1);
      // Auto-play the new greeting
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
          setIsPlaying(true);
        }
      }, 300);
    }
  };
  
  // Listen for mobile widget open event
  const [mobileTriggered, setMobileTriggered] = useState(false);
  useEffect(() => {
    const handleMobileAudioOpen = () => {
      setIsExpanded(true);
      setMobileTriggered(true);
      // Load first greeting if not loaded
      if (!currentGreeting) {
        loadGreeting(GREETING_SECTIONS[0]?.id || 'welcome');
      }
    };
    window.addEventListener("open-mobile-audio", handleMobileAudioOpen);
    return () => window.removeEventListener("open-mobile-audio", handleMobileAudioOpen);
  }, [currentGreeting]);

  // Don't show on admin pages
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
    return null;
  }
  
  const heardCount = getHeardGreetings().length;
  const totalGreetings = GREETING_SECTIONS.length;
  
  return (
    <div 
      className={`fixed bottom-[380px] sm:bottom-[390px] right-2 sm:right-6 left-auto z-[85] transition-all duration-300 kl-scroll-fade ${mobileTriggered ? 'block' : 'hidden md:block'}`}
      data-testid="karine-audio-widget"
    >
      {isExpanded ? (
        <div className="bg-white rounded-2xl shadow-2xl border border-amber-200 p-3 sm:p-4 w-72 sm:w-80 animate-fade-in">
          {/* Close button */}
          <button 
            onClick={() => {
              setIsExpanded(false);
              setMobileTriggered(false);
              if (audioRef.current) audioRef.current.pause();
              setIsPlaying(false);
            }}
            className="absolute -top-2 -right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-500 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
          
          {/* Header */}
          <div className="flex items-center gap-2 sm:gap-3 mb-3">
            <img 
              src="/karine-headshot.jpg" 
              alt="Karine" 
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover object-top border-2 border-amber-300"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-xs sm:text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                Karine Lauzon RN
              </p>
              <p className="text-[10px] sm:text-xs text-gray-500">
                {currentGreeting 
                  ? (language === "fr" ? currentGreeting.titleFr : currentGreeting.title)
                  : (language === "fr" ? "Message personnel" : "Personal message")}
              </p>
            </div>
            {/* Next greeting button */}
            <button
              onClick={playNextGreeting}
              disabled={isLoading}
              className="w-8 h-8 rounded-full bg-amber-100 hover:bg-amber-200 flex items-center justify-center text-amber-700 transition-colors disabled:opacity-50"
              title={language === "fr" ? "Autre message" : "Next message"}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Audio player */}
          <div className="bg-amber-50 rounded-xl p-2 sm:p-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={togglePlay}
                  className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all flex-shrink-0 active:scale-95 ${
                    isPlaying 
                      ? "bg-amber-500 text-white shadow-lg" 
                      : "bg-amber-200 text-amber-700 hover:bg-amber-300"
                  }`}
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-amber-800 mb-1">
                    {isPlaying 
                      ? (language === "fr" ? "Karine parle..." : "Karine speaking...") 
                      : (language === "fr" ? "Appuyez pour écouter" : "Tap to listen")}
                  </p>
                  {/* Progress bar */}
                  <div className="h-1.5 bg-amber-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Greeting counter */}
          <div className="flex items-center justify-between mt-2 px-1">
            <p className="text-[10px] text-gray-400">
              {heardCount}/{totalGreetings} {language === "fr" ? "messages écoutés" : "messages heard"}
            </p>
            <button
              onClick={playNextGreeting}
              className="text-[10px] text-amber-600 hover:text-amber-700 font-medium"
            >
              {language === "fr" ? "Suivant →" : "Next →"}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsExpanded(true)}
          className={`group flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl active:scale-95 bg-white text-gray-700 hover:bg-amber-50 border border-amber-200`}
        >
          <div className="relative">
            <Headphones className="w-4 h-4 sm:w-5 sm:h-5" />
            {heardCount < totalGreetings && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            )}
          </div>
          <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
            {language === "fr" ? "Écoutez Karine" : "Meet Karine Audio"}
          </span>
          {heardCount > 0 && heardCount < totalGreetings && (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
              {totalGreetings - heardCount} {language === "fr" ? "nouveaux" : "new"}
            </span>
          )}
        </button>
      )}
    </div>
  );
};

// Larger audio player for About page
export const KarineAudioPlayer = ({ className = "" }) => {
  const { language } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  
  useEffect(() => {
    const audio = new Audio(DEFAULT_AUDIO_URL);
    audioRef.current = audio;
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };
    
    const handleTimeUpdate = () => {
      setProgress((audio.currentTime / audio.duration) * 100);
      setCurrentTime(audio.currentTime);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };
    
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    
    if (audio.duration) {
      setDuration(audio.duration);
    }
    
    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);
  
  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className={`bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 sm:p-5 border border-amber-200 ${className}`} data-testid="karine-audio-player">
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="relative flex-shrink-0">
          <img 
            src="/karine-headshot.jpg" 
            alt="Karine Lauzon RN" 
            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover object-top border-2 border-amber-300 shadow-md"
          />
          {isPlaying && (
            <span className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-amber-500 rounded-full flex items-center justify-center">
              <Volume2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white animate-pulse" />
            </span>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm sm:text-base mb-0.5 sm:mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            {language === "fr" ? "Message de Karine" : "A Message from Karine"}
          </p>
          <p className="text-[10px] sm:text-xs text-gray-600 mb-2 sm:mb-3">
            {language === "fr" 
              ? "Découvrez qui nous sommes et ce que nous faisons" 
              : "Learn about who we are and what we do"}
          </p>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={togglePlay}
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0 active:scale-95 ${
                isPlaying 
                  ? "bg-amber-500 text-white shadow-lg" 
                  : "bg-amber-200 text-amber-700 hover:bg-amber-300 active:bg-amber-400"
              }`}
            >
              {isPlaying ? <Pause className="w-4 h-4 sm:w-5 sm:h-5" /> : <Play className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5" />}
            </button>
            
            <div className="flex-1">
              <div className="h-1.5 sm:h-2 bg-amber-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[9px] sm:text-[10px] text-gray-500">{formatTime(currentTime)}</span>
                <span className="text-[9px] sm:text-[10px] text-gray-500">{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KarineAudioButton;
